import { useEffect, useRef, useState } from 'react';

// PrimeReact components
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';

// App imports
import { defaultPageRowOptions, defaultRowOptions, SkeletonbodyTemplate, Skeletonitems, verificationOptions } from '../../../../appconfig/Settings';
import { getAllDetailsDirect, getSpecificDetailsById, postAllData } from '../../../../service/ProductService';
import { CANCELLATIONLETTER, DOWNLOADCANCELLATIONLETTER, STORECANCELLATIONLETTER } from '../../../../config/ApiConstant';
import { FalseAndFakeListColumns } from '../../../../appconfig/DatatableSetting';
import { amountTemplate, dateTemplate } from '../../../../appconfig/DatatableTemplateSetting';
import { LettersTab } from '../../LettersTab';
import { CancellationTab } from '../CancellationLetterTab';
import Loader from '../../../../components/Loadeer/Loader';
import { LetterShowClick, validateFalseAndFakeLetterForm } from '../../../../config/Validate';

export const FalseAndFakeLetter = () => {
    document.title = 'False And Fake Letter | GSPC';

    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [pageLoad, setPageLoad] = useState<boolean>(true);
    const [errors, setErrors] = useState<any>({});
    const [regNo, setRegNo] = useState<any>(null);
    const [falseAndFakeLetterData, setFalseAndFakeLetterData] = useState<any>({
        Verification: null,
        IssuedbyName: '',
    });
    const [addLoader, setAddLoader] = useState<boolean>(false);
    const [falseAndFakeLetterList, setFalseAndFakeLetterList] = useState<any>([]);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [buttonVisible, setButtonVisible] = useState<boolean>(true);

    useEffect(() => {
        getLetterList();
    }, []);

    const leftToolbarTemplate = () => (
        <span className="search-bar mr-3">
            <i className="pi pi-search"></i>
            <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
        </span>
    );

    const rightToolbarTemplate = () => (
        <Button icon="pi pi-filter-slash" label="Clear" onClick={() => clearAllFilter()} className="p-button-outlined" />
    );

    const clearAllFilter = () => {
        setGlobalFieldFilter(null);
        setGlobalFilter(null);
    };

    const clearAll = () => {
        setRegNo(null);
        setFalseAndFakeLetterData({
            Name: '',
            ToAddress: '',
            Verification: null,
            IssuedbyName: '',
        });
        setErrors({});
        setButtonVisible(true);
    };

    const getLetterList = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetailsDirect(CANCELLATIONLETTER + '?cancelby=false_fake&search=');
            if (response) {
                const { List } = response;
                setFalseAndFakeLetterList(List);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could Not Load Data' });
        } finally {
            setPageLoad(true);
        }
    };

    const getUserDetails = async () => {
        try {
            const { errors, isError } = LetterShowClick(regNo);
            setErrors(errors);
            if (!isError) {
                setSearchLoading(true);
                const response = await getAllDetailsDirect(CANCELLATIONLETTER + '?cancelby=false_fake&FileNo=' + regNo);
                if (response && Object.keys(response?.Details || {}).length !== 0) {
                    const { StudF, StudM, StudL, postAddress, RegNo, PPhone, postDist, postPIN } = response.Details;
                    let FromNm = `${StudL || ''} ${StudF || ''} ${StudM || ''}`.trim();
                    let address =
                        (!window.cn(postAddress) ? postAddress : "") +
                        ", " +
                        (!window.cn(postDist) ? postDist : "") +
                        "-" +
                        (!window.cn(postPIN) ? postPIN : "");
                    setFalseAndFakeLetterData({
                        ...falseAndFakeLetterData,
                        Name: FromNm,
                        ToAddress: address,
                        RegNo: RegNo,
                        PPhone: PPhone
                    });
                    setButtonVisible(false);
                } else {
                    setButtonVisible(true);
                }
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could Not Load Data' });
        } finally {
            setSearchLoading(false);
        }
    };

    const onInputChange = (e: any, name: string) => {
        let val = e.target?.value || e.value || '';
        setFalseAndFakeLetterData({ ...falseAndFakeLetterData, [name]: val });
    };

    // Handle False and Fake Letter Submission
    const addFakeAndFalseLetterData = async () => {
        try {
            const { errors, isError } = validateFalseAndFakeLetterForm(regNo, falseAndFakeLetterData);
            setErrors(errors);
            if (isError) return;

            const requestData = {
                FileNo: Number(regNo),
                Name: falseAndFakeLetterData.Name || '',
                ToAddress: falseAndFakeLetterData.ToAddress || '',
                verification_by: falseAndFakeLetterData.Verification,
                issued_by_name: falseAndFakeLetterData.IssuedbyName,
                RegNo: falseAndFakeLetterData.RegNo || '',
                mobile: falseAndFakeLetterData.PPhone || "",
                cancelby: 'false_fake',
            };

            setAddLoader(true);

            const response = await postAllData(STORECANCELLATIONLETTER, requestData);
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'False & Fake Letter saved successfully' });
                clearAll();
                getLetterList();
                // getUserDetails(); // this will re-fill the form data
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something Went Wrong' });
        } finally {
            setAddLoader(false);
        }
    };

    const actionsTemplate = (rowData: any) => (
        <div className="actions action-btn">
            <Button icon="pi pi-download" onClick={() => downloadFalseAndFakeLetter(rowData?.SrNo)} tooltip="Download" tooltipOptions={{ position: 'top' }} />
        </div>
    );

    const downloadFalseAndFakeLetter = async (id: any) => {
        try {
            setLoading(true);
            const response = await getSpecificDetailsById(DOWNLOADCANCELLATIONLETTER, id);
            if (response?.file_url) {
                const fileName = `false-fake-letter-${new Date().getTime()}.pdf`;
                const link = document.createElement('a');
                link.href = response.file_url;
                link.target = '_blank';
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Download Failed' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <LettersTab mainTabLabel="Cancellation Letters" />
            <CancellationTab initialTabLabel="False&Fake" />
            <Toast ref={toast} />

            <div className="grid crud-demo mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="p-fluid formgrid grid align-items-center">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="regNo">
                                    File No <span style={{ color: 'red' }}>*</span>
                                </label>
                                <InputText
                                    value={regNo || ''}
                                    keyfilter="num"
                                    onChange={(e) => setRegNo(e.target.value)}
                                    placeholder="Enter File No."
                                    className={errors['regNo'] && 'p-invalid'}
                                />
                                <small className="p-invalid-txt">{errors['regNo']}</small>
                            </div>
                            <div className="field col-12 md:col-1 mt-4">
                                <Button label="Search" className="p-button-primary" onClick={getUserDetails} loading={searchLoading} />
                            </div>
                        </div>

                        <hr />

                        <div className="p-fluid formgrid grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="Name">Name</label>
                                <InputText value={falseAndFakeLetterData?.Name || ''} name="Name" disabled />
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="MobileNo">Mobile No</label>
                                <InputText value={falseAndFakeLetterData?.PPhone || ''} keyfilter="num" name="MobileNo" disabled />
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="ToAddress">Address</label>
                                <InputTextarea value={falseAndFakeLetterData?.ToAddress || ''} rows={4} onChange={(e) => onInputChange(e, 'ToAddress')} />
                            </div>
                        </div>

                        <div className="p-fluid formgrid grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="Verification">
                                    Verification <span style={{ color: 'red' }}>*</span>
                                </label>
                                <Dropdown
                                    value={falseAndFakeLetterData?.Verification}
                                    options={verificationOptions}
                                    onChange={(e) => onInputChange(e, 'Verification')}
                                    placeholder="Select Verification"
                                    className={errors['Verification'] && 'p-invalid'}
                                />
                                <small className="p-invalid-txt">{errors['Verification']}</small>
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="IssuedbyName">
                                    Issued by Name <span style={{ color: 'red' }}>*</span>
                                </label>
                                <InputText
                                    value={falseAndFakeLetterData?.IssuedbyName || ''}
                                    onChange={(e) => onInputChange(e, 'IssuedbyName')}
                                    placeholder="Enter Issued by Name"
                                    className={errors['IssuedbyName'] && 'p-invalid'}
                                />
                                <small className="p-invalid-txt">{errors['IssuedbyName']}</small>
                            </div>
                        </div>

                        <div className="formgrid grid">
                            <div className="field col-12 md:col-12" style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary" onClick={clearAll} />
                                <Button label="Save" icon="pi pi-check" onClick={addFakeAndFalseLetterData} loading={addLoader} disabled={buttonVisible} />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {pageLoad ? (
                            <DataTable
                                value={falseAndFakeLetterList}
                                showGridlines
                                stripedRows
                                globalFilter={globalFilter}
                                filters={globalFieldFilter}
                                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                rows={defaultRowOptions}
                                paginator={falseAndFakeLetterList.length >= 10}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm"
                                emptyMessage="No Data Found"
                                removableSort
                            >
                                {FalseAndFakeListColumns.map((col) => {
                                    if (col.field === 'action')
                                        return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                    if (col.field === 'created_at')
                                        return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} />;
                                    if (col.field === 'amount')
                                        return <Column key={col.field} field={col.field} header={col.header} body={amountTemplate} />;
                                    return (
                                        <Column
                                            key={col.field}
                                            field={col.field}
                                            header={col.header}
                                            sortable
                                            filter
                                            filterPlaceholder={'Search by ' + col.header}
                                        />
                                    );
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                {FalseAndFakeListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>
            {loading && <Loader />}
        </>
    );
};
